import type { Scene } from 'cesium';

/**
 * Why a scene needs `pickTranslucentDepth`:
 * - `underground`: picking through the translucent globe with Google 3D tiles (UndergroundControls).
 * - `splat`: Gaussian splats write no depth, so position picks (and the camera controller's
 *   grab point) only find them with it.
 * Each extra depth-only render costs a frame's worth of work on every `pickPosition`, so the
 * flag is only on while at least one reason applies.
 */
export type TranslucentDepthReason = 'underground' | 'splat';

const reasonsByScene = new WeakMap<Scene, Set<TranslucentDepthReason>>();

export function setTranslucentDepthPicking(scene: Scene, reason: TranslucentDepthReason, needed: boolean) {
	const reasons = reasonsByScene.get(scene) ?? new Set<TranslucentDepthReason>();
	if (needed) reasons.add(reason);
	else reasons.delete(reason);
	reasonsByScene.set(scene, reasons);
	scene.pickTranslucentDepth = reasons.size > 0;
}

/** A shown tileset marked `isSplat` by the code that loaded it (TilesLoader, Set Location). */
export function hasVisibleSplat(scene: Scene): boolean {
	const { primitives } = scene;
	for (let index = 0; index < primitives.length; index++) {
		const primitive = primitives.get(index) as { isSplat?: boolean; show?: boolean } | undefined;
		if (primitive?.isSplat === true && primitive.show !== false) return true;
	}
	return false;
}

/**
 * Re-checks the splat reason before every frame, so layers loaded, shown or hidden anywhere
 * (layer list, Set Location) are picked up without wiring each of them. Returns the remover.
 */
export function trackSplatsForDepthPicking(scene: Scene): () => void {
	return scene.preRender.addEventListener(() => {
		setTranslucentDepthPicking(scene, 'splat', hasVisibleSplat(scene));
	});
}
